import React, { FC, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import { useSelector } from 'react-redux';
import { Button } from '@inplayer-org/inplayer-ui';
import styled, { css } from 'styled-components';
import { MdFullscreen, MdFullscreenExit } from 'react-icons/md';

import previewImg from 'assets/pics/ip-preview-premium.jpg';

// hooks
import useSharedBoolean from 'pages/MerchantPages/Assets/containers/EditAsset/hooks/useSharedBoolean';
import useSharedObject from 'pages/MerchantPages/Assets/containers/EditAsset/hooks/useSharedObject';
import useSharedState from 'pages/MerchantPages/Assets/containers/EditAsset/hooks/useSharedState';

// types
import { AccessControlType } from 'ducks/merchantDucks/accesses/actions';
import { Details } from 'ducks/merchantDucks/items/types';

// selectors
import { getItemDetails, getTemplateDetails } from 'ducks/merchantDucks/items/selectors';
import { getBranding } from 'ducks/merchantDucks/branding/selectors';
import { getRestrictionsState } from 'ducks/merchantDucks/restrictions/selectors';

// components
import PaywallPreview from 'components/PaywallPreviews/PaywallPreview';
import ItemAnalytics from 'pages/MerchantPages/Assets/components/ItemAnalytics';
import PreviewSlider from 'pages/MerchantPages/Assets/components/PreviewSlider';

// Additional components
import { PreviewWrapper } from 'components/SharedStyledElements';
import { DEFAULT_TEMPLATE_ID } from '../MassiveEditAsset';

const sharedStyle = css`
  position: absolute;
  left: 1rem;
  z-index: 1;
`;

const PreviewButton = styled(Button)`
  ${sharedStyle}
  transition: width 0.5s;
  top: 1rem;
`;

const AssetPreview: FC = () => {
  const [isAnalyticsView] = useSharedBoolean('isAnalyticsView');
  const [useDefaultBrandingTheme] = useSharedBoolean('useDefaultBrandingTheme');
  const [accordionHidden, setAccordionHidden] = useSharedBoolean('accordionHidden');
  const [isPreviewTemplateOpen] = useSharedBoolean('isPreviewTemplateOpen');
  const [useDefaultTemplateId] = useSharedBoolean('useDefaultTemplateId');
  const [previewIndex] = useSharedState('previewIndex', 1);
  const [previewTemplateId] = useSharedState('previewTemplateId', DEFAULT_TEMPLATE_ID);
  const [previewFields] = useSharedObject<{ title?: string; description?: string; buttonLabel?: string }>(
    'previewFields',
    {}
  );

  const [heightPercent, setHeightPercent] = useState(55);
  const [widthPercent, setWidthPercent] = useState(80);

  const { pathname } = useLocation();
  const isEditTemplate = pathname.includes('templates');
  const assetDetails = useSelector(getItemDetails);
  const templateDetails = useSelector(getTemplateDetails);
  const { defaultBrandingSet } = useSelector(getBranding);
  const {
    assetCountrySet: { id: assetCountrySetId },
    assetDomainsInputs: { inputs: assetDomainRestrictions },
  } = useSelector(getRestrictionsState);

  const {
    accessFees = [],
    metadata,
    access_control_type: { id: accessControlTypeId },
  } = useMemo(() => (isEditTemplate ? templateDetails : assetDetails) as Details, [
    pathname,
    assetDetails,
    templateDetails,
  ]);

  const toggleFullScreen = () => {
    setAccordionHidden(!accordionHidden);
    if (accordionHidden) {
      setWidthPercent(80);
      setHeightPercent(55);
    } else {
      setTimeout(() => {
        setWidthPercent(85);
      }, 500);
      setHeightPercent(85);
    }
  };

  const { buttonLabel, description, title } = previewFields;
  // Preview details from metadata
  const { paywall_cover_photo } = metadata;

  let brandingSet = {
    ...metadata,
    paywall_cover_photo,
    preview_button_label: buttonLabel,
    preview_description: description,
    preview_title: title,
  };

  brandingSet = {
    ...brandingSet,
    ...defaultBrandingSet,
    paywall_cover_photo: defaultBrandingSet.paywall_cover_photo || previewImg,
  };

  const { template_id: brandingTemplateId } = defaultBrandingSet;

  const showPreviewButton =
    isPreviewTemplateOpen && previewIndex === AccessControlType.PAID && !!previewTemplateId && previewTemplateId !== 1;

  return (
    <>
      {showPreviewButton && (
        <PreviewButton
          type="button"
          size="sm"
          onClick={toggleFullScreen}
          icon={accordionHidden ? <MdFullscreenExit /> : <MdFullscreen />}
        >
          {accordionHidden ? 'Close fullscreen' : 'View fullscreen'}
        </PreviewButton>
      )}
      <PreviewWrapper
        isFullWidth={accordionHidden}
        isAnalyticsView={isAnalyticsView}
        shouldSetMargin={previewTemplateId === 7 || previewIndex === 5}
      >
        {accordionHidden && (
          <PreviewSlider
            heightPercent={heightPercent}
            widthPercent={widthPercent}
            setHeightPercent={setHeightPercent}
            setWidthPercent={setWidthPercent}
          />
        )}
        {!isAnalyticsView ? (
          <PaywallPreview
            accessFees={accessFees}
            previewIndex={previewIndex}
            branding={brandingSet}
            assetCountrySetId={assetCountrySetId}
            assetDomainRestrictions={assetDomainRestrictions}
            displayBuyButton
            accessControlTypeId={accessControlTypeId}
            width={accordionHidden ? `${widthPercent}%` : undefined}
            height={accordionHidden ? `${heightPercent}%` : undefined}
            previewTemplateId={useDefaultBrandingTheme || useDefaultTemplateId ? brandingTemplateId : previewTemplateId}
            isTemplate={isEditTemplate}
          />
        ) : (
          <ItemAnalytics />
        )}
      </PreviewWrapper>
    </>
  );
};

export default AssetPreview;
